/**
 * Phase 22 — Subject form portal type contracts.
 * Phase 328 wave 4 — the DECLARATIVE INTERACTIVE FORM contract, client half.
 *
 * `form_schema` is an OPAQUE passthrough of the workflow declaration's `forms.<id>` block:
 * nothing engine-side interprets its interior, so the grammar has exactly two homes —
 * `packages/workflow-schema/src/schema/form-grammar.ts` (the declaration + deploy-time
 * validation, rules ARD-DSL-036/037/038 and, since Phase 532, ARD-DSL-051 for the `from_ctx`
 * pre-answer block) and THIS FILE (the renderer's half). The two are
 * mirrored 1:1 on purpose: widening one without the other is how a declaration becomes
 * deploy-valid but unrenderable. There is no third copy.
 *
 * Everything here is ADDITIVE over the Phase-22 flat single-page contract:
 *  - `FormSchema.fields` is the legacy flat list and still renders exactly as it always has;
 *  - `FormSchema.steps` is optional — its ABSENCE is the legacy contract;
 *  - a schema with neither key (a description-only form) is valid and renders as
 *    title + description + a neutral empty state.
 */

/** The five field types the flat single-page contract has always supported. */
export type LegacyFieldType = "string" | "email" | "textarea" | "select" | "date";

/** The Phase-328 additions (mirrors INTERACTIVE_FORM_FIELD_TYPES). */
export type InteractiveFieldType =
  | "number"
  | "boolean"
  | "tel"
  | "radio"
  | "checkbox_group"
  | "group"
  | "address"
  | "url";

/** KYC-design — the closed set of UK identifier presets (mirrors FORM_FORMAT_PRESETS). */
export type FieldFormat =
  | "crn"
  | "utr"
  | "vat_number"
  | "postcode"
  | "paye_ref"
  | "accounts_office_ref"
  | "ni_number"
  | "sort_code"
  | "account_number"
  | "auth_code"
  | "sic_codes";

/**
 * client walk, 6 Oct 2026 — a field's reference link (`help_link`, ARD-DSL-080): PLAIN-TEXT words
 * and an absolute https URL. Opened in a new tab; anything that is not absolute https is dropped.
 */
export interface HelpLink {
  label: string;
  url: string;
}

/** KYC-design — the structured address an `address` field submits. */
export interface AddressValue {
  line1?: string;
  line2?: string;
  town?: string;
  county?: string;
  postcode?: string;
}
export const ADDRESS_KEYS: readonly (keyof AddressValue)[] = ["line1", "line2", "town", "county", "postcode"];

/**
 * Phase 541 — POSTCODE LOOKUP for an `address` block, the renderer's half of
 * `FORM_ADDRESS_LOOKUP_KINDS` / `FormAddressLookup` (packages/workflow-schema, form-grammar.ts).
 *
 * Mirrored BY HAND, like everything else in this file and for the same reason (the "exactly two
 * homes" block at the top): the SPA deliberately does not depend on `@ardept/workflow-schema` —
 * it must not bundle the deploy-time validator — so the grammar and this file are the key's two
 * homes, kept 1:1 on purpose.
 *
 * A LIST of one, not a boolean, because international lookup is a second entry here rather than
 * a second key on the field. ADDITIVE and OPT-IN BY PRESENCE: a field that declares no `lookup`
 * renders byte-identically to today.
 */
export const FORM_ADDRESS_LOOKUP_KINDS = ["postcode"] as const;
export type FormAddressLookup = (typeof FORM_ADDRESS_LOOKUP_KINDS)[number];

/**
 * The RESERVED `lookup_id` the surface posts to `POST /forms/v0/lookups` for an address lookup
 * (mirrors `ADDRESS_LOOKUP_RESERVED_LOOKUP_ID`). It is RESERVED: no declared `lookups:` entry may
 * take it, because the server synthesises that declaration from this id alone — which is why the
 * client can name it without naming an integration, a request shape or a connector.
 */
export const ADDRESS_LOOKUP_RESERVED_LOOKUP_ID = "address_lookup";

/** Every SINGLE-VALUE field type — one input, one value in the submitted payload. */
export type ScalarFieldType = LegacyFieldType | Exclude<InteractiveFieldType, "group">;

/** Every declarable field type: the scalars plus the repeating `group`. */
export type FieldType = ScalarFieldType | "group";

/**
 * Field types whose rendering is driven by a closed choice list, so `options` is mandatory
 * (enforced at deploy time by ARD-DSL-036).
 */
export const CHOICE_FIELD_TYPES: readonly ScalarFieldType[] = ["select", "radio", "checkbox_group"];

// ── Bounded conditional-visibility predicates (ARD-DSL-037) ────────────────────────────────
//
// A `visible_when` is a BOUNDED STRUCTURE, never an evaluated expression: there is no string
// grammar to parse and nothing to `eval`. The evaluator lives in `schema/predicates.ts`.

/** The CLOSED comparison-operator whitelist (mirrors FORM_PREDICATE_OPS). */
export type PredicateOp = "eq" | "ne" | "in" | "truthy";

/** A declared comparison value: scalar for `eq`/`ne`, an array for `in`. */
export type PredicateScalar = string | number | boolean;

/** One bounded comparison. `path` is `fields.<declared_name>` or `ctx.<dotted.path>`. */
export interface PredicateLeaf {
  path: string;
  op: PredicateOp;
  /** Required for `eq`/`ne` (scalar) and `in` (non-empty array); absent for `truthy`. */
  value?: PredicateScalar | PredicateScalar[];
}

/** `all: []` — every child predicate must hold (logical AND). */
export interface PredicateAll {
  all: VisibleWhen[];
}

/** `any: []` — at least one child predicate must hold (logical OR). */
export interface PredicateAny {
  any: VisibleWhen[];
}

/** A leaf, or an `all`/`any` combinator, nested at most PREDICATE_MAX_DEPTH levels deep. */
export type VisibleWhen = PredicateLeaf | PredicateAll | PredicateAny;

// ── Field specs ────────────────────────────────────────────────────────────────────────────

/**
 * Phase 449 — one entry of a choice field's `options` list, in its LABELLED form
 * (mirrors FormFieldOption).
 *
 * `value` is the WIRE TOKEN: it is what the submission carries, and it is the PREDICATE
 * COMPARAND — a `visible_when` `eq` / `in` compares against `value`, NEVER against the label
 * (`schema/predicates.ts` never sees a label). Relabelling a choice is always safe; changing
 * a value is a wire break.
 *
 * `label` is display copy, PLAIN TEXT like `help` — the renderer never injects
 * declaration-supplied markup. ABSENT ⇒ the value is humanised, which is also exactly what a
 * BARE STRING entry gets: `"psc"` is the label-less form of `{ value: "psc" }`, so every
 * options list declared before this phase keeps byte-identical meaning.
 */
export interface FieldOption {
  /** The wire/payload token AND the `visible_when` `eq`/`in` comparand. Never the label. */
  value: string;
  /** Display copy for the subject. ABSENT ⇒ the renderer humanises `value`. */
  label?: string;
  /** KYC-design — one line under the label on a choice card. */
  description?: string;
}

// ── Declarative pre-answer from the job's own ctx (ARD-DSL-051) ────────────────────────────
//
// Phase 532. Mirrors `FormFieldFromCtx` in form-grammar.ts. The RESOLUTION is server-side —
// one shared resolver behind both form-serving doors — so the renderer never reads `from`,
// `combine`, `rank` or `map`: it reads the RESOLVED keys the served schema carries beside them
// (`from_ctx_value`, `from_ctx_source_label`, `from_ctx_unresolved_reason`, `evidence[].value`).
// The declared shape is mirrored here anyway, for the same reason the rest of this file is: a
// key the renderer's type does not carry is a key a declaration can be deploy-valid with and
// still be unrenderable.

/**
 * Phase 559 (card #2116, L-97) — WHAT A DISPLAY ROW SHOWS. Mirrors `FORM_ROW_SHOWS_VALUES` in
 * form-grammar.ts, by hand, for the reason the block at the top of this file gives.
 *
 *   `words`     — a scalar read through the row's own `words` map (`check_failed` ⇒ "Could not
 *                 be checked"), so a raw ctx token never reaches a human.
 *   `number`    — a scalar rendered as a number.
 *   `text`      — a scalar rendered as text: the EXPLICIT form of today's default row.
 *   `ledger`    — a STRUCTURED value laid out by `presentation`, instead of the generic
 *                 `Key: Value` dump that put 28 unreadable lines on the approval page.
 *   `per_party` — a NAMED per-party projection (`[{party, value}, …]`), one line per party.
 */
export const FORM_ROW_SHOWS_VALUES = ["words", "number", "text", "ledger", "per_party"] as const;
export type FormRowShows = (typeof FORM_ROW_SHOWS_VALUES)[number];

/** How a `shows: ledger` row is laid out (mirrors `FORM_ROW_PRESENTATION_KINDS`). */
export const FORM_ROW_PRESENTATION_KINDS = ["ledger_line"] as const;
export type FormRowPresentationKind = (typeof FORM_ROW_PRESENTATION_KINDS)[number];

/**
 * A display row's VALUE → PLAIN ENGLISH map. The keys are STRINGS — a boolean ctx value is
 * looked up as `String(value)` (`true` finds the quoted key `"true"`), the same normalisation
 * the deploy-time rank check applies — and the map is deliberately PARTIAL: a value it does not
 * carry renders the row's `otherwise`, else "not recorded", never the raw token.
 */
export type FormRowWords = Record<string, string>;

/**
 * Walk 2 — the closed set of TONES a display row's `tones` map may name (mirrors
 * `FORM_ROW_TONE_VALUES`, packages/workflow-schema). Each is coloured by a design-system
 * feedback token: `good` → success, `caution` → warning, `bad` → error.
 */
export const FORM_ROW_TONE_VALUES = ["good", "caution", "bad"] as const;
export type FormRowTone = (typeof FORM_ROW_TONE_VALUES)[number];
/** Walk 2 — VALUE → TONE, looked up as `String(value)` like `words`. PARTIAL. */
export type FormRowTones = Record<string, FormRowTone>;

/**
 * ONE COLUMN of a `ledger_line` presentation: which key of a ledger ELEMENT it reads, and the
 * map that turns that value into plain English. `path` is an element key (`input`, `weighted`),
 * NOT a `${ctx.…}` template — the renderer never walks a path. No element key is ever humanised
 * into a caption, which is what stops `source_path` (a literal `${ctx…}` string) being shown.
 */
export interface FormRowLedgerColumn {
  path: string;
  words?: FormRowWords;
}

/** How a `shows: ledger` row lays out one element: label · source · signed points. */
export interface FormRowLedgerPresentation {
  kind: FormRowPresentationKind;
  columns: {
    label: FormRowLedgerColumn;
    source?: FormRowLedgerColumn;
    points: { path: string };
  };
  /** Drop the lines that scored nothing. ABSENT ⇒ `true` (resolved by the renderer). */
  hide_zero?: boolean;
}

/** Which element keys of a per-party projection carry the name and the value. */
export interface FormRowPerParty {
  /** ABSENT ⇒ `party`. */
  name?: string;
  /** ABSENT ⇒ `value`. */
  value?: string;
}

/**
 * One DISPLAY-ONLY evidence row, as DECLARED: a caption, the `${ctx.…}` path behind it and —
 * since Phase 559 — what the row SHOWS. Everything but the caption and the path is OPTIONAL:
 * a row declaring none of it renders exactly as it did before.
 */
export interface FieldEvidenceRef {
  label: string;
  path: string;
  /** ABSENT ⇒ today's scalar row. */
  shows?: FormRowShows;
  /** Value → plain English. Armed by `shows: words` / `shows: per_party`. */
  words?: FormRowWords;
  /** What an unmapped value reads as. ABSENT ⇒ "not recorded" — never the raw token. */
  otherwise?: string;
  /** How a structured value is laid out. Armed by `shows: ledger`. */
  presentation?: FormRowLedgerPresentation;
  /** Which element keys of a per-party projection carry the name and the value. */
  per_party?: FormRowPerParty;
  /** Walk 2 — value → tone (good / caution / bad), coloured by the design-system feedback tokens. */
  tones?: FormRowTones;
  /**
   * Walk 2 — the caption READ FROM THE FILE: a `${ctx.…}` path whose non-blank text replaces
   * `label` (a company's own name above its screen result). ABSENT or blank ⇒ `label`.
   */
  label_from?: string;
}

/** How a `from_ctx` block collapses its ordered `from` list to one candidate. */
export type FromCtxCombine = "first_present" | "worst_of";

/**
 * Phase 532 — the DECLARED pre-answer block. Choice fields only: the pre-answer has to BE one
 * of the field's own options for the person answering to see it selected and change it.
 *
 * TWO MODES, decided by `map`. PRE-ANSWER (`map` present) serves the mapped option with
 * `source_label` beside it. EVIDENCE-ONLY (`map` absent — and therefore no `rank`/`combine`)
 * never pre-answers: it renders `evidence` plus the declared `unavailable_reason` beside an
 * UNANSWERED question. An unmapped source value is legal and means the same thing — unanswered,
 * with the served reason. It NEVER falls through to the first option.
 */
export interface FieldFromCtx {
  /** Ordered `${ctx.<dotted.path>}` candidates. Resolved SERVER-SIDE; the renderer never walks them. */
  from?: string[];
  /** ABSENT ⇒ `first_present`. */
  combine?: FromCtxCombine;
  /** SOURCE values least → most severe. Present iff `combine: worst_of`. */
  rank?: (string | number | boolean)[];
  /** SOURCE value → one of this field's declared option values. Deliberately PARTIAL. */
  map?: Record<string, string>;
  /** The provenance chip shown beside the pre-answer. PLAIN TEXT. */
  source_label?: string;
  /** The AUTHOR-DECLARED blocker: why this question cannot be pre-answered from the file. */
  unavailable_reason?: string;
  /** DISPLAY-ONLY rows beside the question — never a pre-answer, never in the payload. */
  evidence?: FieldEvidenceRef[];
  /** Walk 2 — the heading above the evidence box ("Screening results"). PLAIN TEXT. */
  evidence_title?: string;
}

/**
 * One RESOLVED evidence row as the SERVED schema carries it — the declared caption and the
 * value the server read out of ctx for it. Deliberately not the same shape as the DECLARED
 * {@link FieldEvidenceRef}: the declaration holds a `path`, the served field holds a `value`,
 * and keeping the two shapes apart is what stops the renderer ever walking a path.
 */
export interface ResolvedEvidence {
  label: string;
  /** Whatever the path resolved to, or null/absent where it resolved to nothing. */
  value?: unknown;
  /**
   * Phase 559 — the DECLARED projection, served VERBATIM beside the value. The row says what it
   * shows at both display sites; a card row carries its declaration to the browser inside the
   * form schema, and an evidence row is resolved server-side, so the resolver copies these keys
   * onto the served row. That is what lets ONE projector serve both sites. Still no `path`: the
   * renderer never walks one.
   */
  shows?: FormRowShows;
  words?: FormRowWords;
  otherwise?: string;
  presentation?: FormRowLedgerPresentation;
  per_party?: FormRowPerParty;
  /** Walk 2 — value → tone (good / caution / bad), coloured by the design-system feedback tokens. */
  tones?: FormRowTones;
  /**
   * Walk 2 — SERVED ONLY, never declared: the legend of the engine's generated code-list row
   * (a `from_ctx.table` field), e.g. "* Matches a cash-intensive sector. Source: …". Present
   * only when a listed item is `marked`.
   */
  legend?: string;
}

/** A single-value field. `options` is mandatory for CHOICE_FIELD_TYPES. */
export interface ScalarFieldSpec {
  /** Payload key AND the `fields.<name>` predicate segment. Unique across the whole form. */
  name: string;
  type: ScalarFieldType;
  required?: boolean;
  /**
   * Choice list — mandatory and non-empty for CHOICE_FIELD_TYPES. Phase 449: an entry is
   * EITHER a `{ value, label }` object or a BARE STRING (the label-less form of the same
   * thing). `schema/formModel.ts` `optionEntries` is the ONE projection of this list into the
   * `{value, label}` pairs every control renders.
   */
  options?: (string | FieldOption)[];
  label?: string;
  placeholder?: string;
  /** Short PLAIN-TEXT guidance rendered UNDER the control and wired to it by `aria-describedby`; absent ⇒ today's markup. */
  help?: string;
  /**
   * client walk, 6 Oct 2026 — ONE reference link shown with the help, opened in a NEW TAB
   * (ARD-DSL-080). The renderer re-checks it is absolute https and draws nothing otherwise.
   */
  help_link?: HelpLink;
  /**
   * Phase 449 — DECLARATIVE CARRY-FORWARD. Three roots (ARD-DSL-036):
   * `fields.<declared top-level field>` (exactly one segment — an answer of THIS form),
   * `party.<key>` (Phase 515 — a key of the whitelisted party snapshot, bare), and
   * `ctx.<dotted.path>` (Phase 549 — a path of the served ctx snapshot, which carries every path
   * a `default_from` names as well as every path a predicate does, so a declared ctx default can
   * no longer resolve to nothing). Each root reads its OWN namespace and never another's.
   *
   * Applied ONLY when this field's key is ABSENT from the answer set, so a field the subject
   * has answered — including one they CLEARED back to `""`, which is present — is never
   * overwritten. Once applied it is an ORDINARY ANSWER: it autosaves and submits like any
   * other. `schema/formModel.ts` `defaultsPatch` is the projection; the caller decides when
   * to apply it. Not declarable on a `group` or on a group subfield.
   */
  default_from?: string;
  /**
   * Phase 532 — DECLARATIVE PRE-ANSWER FROM THE JOB'S OWN CTX (ARD-DSL-051). Choice fields only.
   *
   * Independent of `default_from` (carry-forward from another FIELD of this form) and of a
   * lookup's `populate` (autofill from a connector the BROWSER calls on a trigger field): the
   * three mechanisms coexist and none reads another's state. All three write to the same
   * source-chip channel — one provenance display, three sources.
   */
  from_ctx?: FieldFromCtx;
  /**
   * Phase 532 — the SERVER-RESOLVED half of {@link from_ctx}, written onto the field by the one
   * shared resolver both form-serving doors call. The renderer reads ONLY these keys; it never
   * reads `from` / `combine` / `rank` / `map`, so there is exactly one implementation of the
   * rule and the browser cannot reach a ctx path the declaration did not name.
   *
   * `from_ctx_value` is the PRE-ANSWER: one of this field's own declared option values, or
   * `null` when the file could not answer. Null is NEVER a fall-through to the first option —
   * {@link from_ctx_unresolved_reason} says why, and the question stays unanswered.
   */
  from_ctx_value?: string | null;
  /** The provenance chip shown beside the pre-answer — the SAME channel a lookup autofill uses. */
  from_ctx_source_label?: string | null;
  /** Why there is no pre-answer; the prompt to answer manually. `null` when there IS one. */
  from_ctx_unresolved_reason?: string | null;
  /** The resolved display rows beside the question. Display-only — never an answer, never submitted. */
  evidence?: ResolvedEvidence[];
  /**
   * Walk 2 — the declared `from_ctx.evidence_title`, served verbatim: the heading above the
   * evidence box. PRESENT ⇒ the box reads as one results table (a `per_party` row expands to one
   * line per party). ABSENT ⇒ today's box.
   */
  from_ctx_evidence_title?: string | null;
  /** Hides the field — and excludes it from the payload and required-validation — when false. */
  visible_when?: VisibleWhen;
  format?: FieldFormat;
  pattern?: string;
  message?: string;
  width?: "full" | "half";
  sensitive?: boolean;
  same_as?: string;
  /**
   * Phase 541 — `address` only: offer a POSTCODE LOOKUP on this address block. PRESENT ⇒ the
   * address control renders a search beside the five inputs; a pick fills the five parts as
   * ORDINARY overwritable answers, so the submitted payload shape is unchanged. ABSENT ⇒ today's
   * five hand-typed inputs, byte-identically.
   *
   * Legal on a group SUBFIELD too, which is the case a declared `lookups:` entry structurally
   * cannot serve (a lookup's `populate` targets declared FORM-LEVEL fields).
   */
  lookup?: FormAddressLookup;
  /**
   * Phase 572 wave 3 task 3.2 — a role-locked decision field, SERVED from the engine when the
   * operator cannot change it. The value is a fixed fact (shown, not editable, no options), and
   * the renderer owes no input control. The field's `from_ctx_value` carries the value; this key
   * just says "do not show a control".
   *
   * Deliberately NOT named `read_only`: the grammar's declared `read_only` (wave 5 task 5.1) is a
   * declaration on the form, while this is a SERVED marker computed at request time from the
   * operator's roles. Two concepts, two keys, no confusion.
   */
  locked?: boolean;
  /**
   * Phase 572 wave 3 task 3.2 — the plain-English sentence explaining WHY the field is locked and
   * who may change it. Present only when `locked` is truthy; built from the field's declared label
   * plus the holder emails. Contains no gate id, field name or role token.
   */
  locked_sentence?: string;
  /**
   * Walk 2 — SERVED on a locked decision field from the gate's declared `tones`: the fixed value's
   * tone (good / caution / bad), coloured by the design-system feedback tokens.
   */
  tones?: FormRowTones;
  /**
   * Phase 572 wave 6 task 6.2 — a DECLARED read-only field (mirrors `FormScalarFieldSpec.read_only`,
   * ARD-DSL-036). ADDITIVE and OPT-IN BY PRESENCE: absent ⇒ today's control, byte-identically.
   *
   * PRESENT ⇒ the value its declared source put there (`default_from`, `from_ctx`, a lookup's
   * `populate`) is SHOWN through the one no-control presentation (`ReadOnlyValue`) instead of an
   * input, and is not validated as an input; with NO value the whole row renders nothing and is
   * absent from the review summary. A value-presentation change only: the answer set and the
   * submitted payload are exactly what they would be without this key.
   *
   * Distinct from {@link locked}: that is a SERVED marker computed from the operator's roles on a
   * decision step; this is a declaration on the form. The two meet only at `ReadOnlyValue`.
   */
  read_only?: boolean;
  /**
   * Card #2878 — SUBMITTED, NEVER SHOWN: a `read_only` field the renderer never renders (not on its
   * step, not on the review page) whose value is ALWAYS submitted ("" when nothing filled it) — the LEAP id a pick fills beside
   * the name it shows (form rule 7 / L-97). ARD-DSL-036 pins it to top-level `read_only` fields that
   * are not `required` and carry no `visible_when`.
   */
  hidden?: boolean;
}

/**
 * A REPEATING GROUP: N rows of the same scalar subfields, submitted as an array of objects
 * keyed by subfield name. One level of repetition only — nested groups are rejected at deploy
 * time (ARD-DSL-036).
 */
export interface GroupFieldSpec {
  name: string;
  type: "group";
  /** Scalar subfields only. Names are GROUP-SCOPED (not form-level field names). */
  fields: ScalarFieldSpec[];
  /** Minimum row count (default 0): the subject cannot remove below it, nor submit below it. */
  min?: number;
  /** Maximum row count (unbounded when absent): "add" is disabled at the cap. */
  max?: number;
  required?: boolean;
  label?: string;
  /** Short PLAIN-TEXT guidance for the GROUP as a whole, wired to its `<fieldset>` by `aria-describedby`; each subfield carries its own `help`. */
  help?: string;
  /** client walk, 6 Oct 2026 — the reference link beside the group's help (ARD-DSL-080). */
  help_link?: HelpLink;
  visible_when?: VisibleWhen;
  item_title?: string;
  item_summary?: string;
  add_label?: string;
  /**
   * Phase 563 — pre-tick the filler's OWN row on first render. ADDITIVE and OPT-IN BY PRESENCE:
   * absent ⇒ no matching runs and the group renders exactly as before. Mirrors the grammar's
   * `FormGroupFieldSpec.self_match` 1:1; ARD-DSL-063 validates the relations at deploy time.
   */
  self_match?: GroupSelfMatch;
  /**
   * Walk 3 (ARD-DSL-070) — rows matching `when` render as read-only facts: no inputs, no Edit,
   * no Remove, never validated, cells submitted verbatim. Opt-in by presence.
   */
  locked_rows?: GroupLockedRows;
  /**
   * Card #2506 (ARD-DSL-084) — two rows naming what looks like the same person each say so, with
   * the declared `note` and a one-tap remove of that row. Opt-in by presence.
   */
  likely_duplicates?: GroupLikelyDuplicates;
}

/** Mirrors the grammar's `FormGroupLikelyDuplicates` 1:1. */
export interface GroupLikelyDuplicates {
  /** A string-class subfield of this group holding each row's person name. */
  name_field: string;
  /** Plain-text line under the prompt saying why a person is listed once. */
  note: string;
}

/** Mirrors the grammar's `FormGroupLockedRows` 1:1. */
export interface GroupLockedRows {
  /** Row predicate over `row.<subfield>` leaves. */
  when: VisibleWhen;
  eyebrow: string;
  note?: string;
  /** Subfield names shown as "Label: value" facts; blank values are omitted. */
  facts: string[];
}

/**
 * Mirrors the grammar's `FormGroupSelfMatch` 1:1 — the three naming keys REQUIRED strings naming
 * declared fields. When EXACTLY ONE row's `row_field` carries the filler's forename and surname
 * tokens, that row's `tick_field` is set `true` once; the answer stays ordinary and overwritable.
 */
export interface GroupSelfMatch {
  /** A declared top-level field of this form holding the filler's own full name. */
  filler_field: string;
  /** A string-class subfield of this group holding each row's person name. */
  row_field: string;
  /** A `boolean` subfield of this group ticked on the one matching row. */
  tick_field: string;
  /**
   * Card #3086 — OPTIONAL `{ <subfield of this group>: <top-level field> }`: the blank cells of
   * the row the tick lands on are filled from those top-level answers in the tick's own write —
   * the one matched row on first render, or (card #3090) a row the subject ticks by hand; no
   * other row is touched — and an untick clears each one still equal to its source
   * (`selfMatchSeed` / `selfMatchUnseed` in formModel). Absent ⇒ nothing is seeded.
   */
  seed_from?: Record<string, string>;
}

export type FieldSpec = ScalarFieldSpec | GroupFieldSpec;

// ── Declarative connector-backed lookups (ARD-DSL-038) ─────────────────────────────────────
//
// Mirrored here so the schema type stays 1:1 with the declaration. The lookup CONTROL is
// wave 5's surface; wave 4's renderer carries the shape and ignores it.

/**
 * Phase 523 — ONE source array feeding a `populate_group`'s target group (mirrors
 * FormLookupPopulateGroupSource).
 *
 * `where` is the form's ONE predicate grammar — the same `VisibleWhen` union a field's
 * `visible_when` uses — with one difference that is declared rather than conventional: its leaf
 * paths are `item.`-rooted, because at filter time there is no form field in scope, only the
 * envelope row being considered.
 */
export interface LookupPopulateGroupSource {
  /** Dotted path into the connector envelope resolving to an array. */
  from: string;
  /** OPTIONAL per-item filter; an item it rejects contributes NO row. ABSENT ⇒ every item does. */
  where?: VisibleWhen;
  /** Per-row subfield mapping: `{ <group subfield name>: <item.<path> | literal> }`. */
  map: Record<string, string>;
}

/**
 * The group half of a lookup's response mapping, in its TWO shapes (exactly one per declaration,
 * ARD-DSL-038):
 *   SINGLE-SOURCE — `{target, from, map}`, the Phase 328 shape, valid and unchanged;
 *   MULTI-SOURCE  — `{target, sources: [{from, where?, map}, …], merge_key?}` (Phase 523).
 *
 * `from` / `map` are therefore optional ON THE TYPE and not in practice — the exactly-one-of rule
 * is a relation between keys and lives in the deploy-time validator, exactly as it does on the
 * declaration side (`FormLookupPopulateGroup`).
 */
export interface LookupPopulateGroup {
  /** Names a declared field whose `type` is `group`. */
  target: string;
  /** SINGLE-SOURCE: dotted path into the connector envelope resolving to an array. */
  from?: string;
  /** SINGLE-SOURCE: per-row subfield mapping: `{ <group subfield name>: <item.<path> | literal> }`. */
  map?: Record<string, string>;
  /**
   * MULTI-SOURCE: N envelope arrays feeding ONE group, evaluated in DECLARATION ORDER — order is
   * load-bearing, because a list-valued subfield unions its values first-seen on collapse.
   */
  sources?: readonly LookupPopulateGroupSource[];
  /**
   * Names a subfield of `target` whose NORMALISED value identifies one row across sources:
   * candidates sharing it collapse into ONE row. ABSENT ⇒ no collapse.
   */
  merge_key?: string;
}

/**
 * Phase 524 — THE PICK: a lookup whose upstream returns MANY candidates and the subject chooses
 * one. Mutually exclusive with `show` (one lookup renders one result presentation), and it carries
 * its OWN `populate` so there is exactly one place per lookup where autofill is declared.
 *
 * `from` is rooted at the envelope (`response.…`) like every other lookup path; `title`,
 * `subtitle`, each `rows[].path` and each `populate` value are rooted at `item.` — the same
 * convention `populate_group.map` already uses, so there is one path grammar, not two.
 */
export interface LookupChoose {
  /** Dotted envelope path resolving to the ARRAY of candidates. */
  from: string;
  /** Plain-text line above the list. ABSENT ⇒ the renderer's generic prompt. */
  eyebrow?: string;
  /** `item.<path>` — the headline of one candidate. */
  title: string;
  /** `item.<path>` — one line under the headline. */
  subtitle?: string;
  /** Ordered labelled rows under each candidate's title; each `path` is `item.`-rooted. */
  rows?: ReadonlyArray<{ label: string; path: string }>;
  /**
   * Card #2717 — the CANDIDATE FILTER: an item of `from` this predicate rejects is not offered.
   * The SAME `VisibleWhen` grammar and evaluator a `populate_group` source's `where` uses, with
   * `item.`-rooted leaves. ABSENT ⇒ every item is offered, byte-identical to Phase 524.
   */
  where?: VisibleWhen;
  /** What the PICK fills in: `{ <declared scalar field>: <item.<path> | literal> }`. */
  populate: Record<string, string>;
  /**
   * Phase 561 — ONE PICK, ONE CHAIN: the `id` of another lookup on the SAME step. On the
   * renderer's side, CONFIRMING a candidate applies this pick's `populate` and then runs the
   * named lookup straight away with the just-picked values — the subject makes one gesture and
   * reads one result card. The chained lookup is run BY the chain, so the renderer suppresses
   * its own control and its standalone result card; nothing but this key drives that.
   *
   * ABSENT ⇒ a confirmed pick only fills in `populate`, byte-identical to Phase 524.
   */
  then_run?: string;
}

export interface FormLookup {
  /** The `lookup_id` the client posts; the declaration is resolved SERVER-SIDE from it. */
  id: string;
  /** Names a declared top-level integration. The client can never supply this. */
  integration_id: string;
  trigger_field: string;
  request?: Record<string, string>;
  populate?: Record<string, string>;
  populate_group?: LookupPopulateGroup;
  /**
   * Phase 449 — AUTOFILL VISIBILITY COPY: what the subject reads on each step this lookup
   * populated. A lookup routinely fills fields the subject is not looking at (a later step, a
   * repeating group several steps on), so the trigger step's status line is not where the news
   * belongs. PLAIN TEXT, like `help`. ABSENT ⇒ the renderer's generic notice copy.
   */
  notice?: string;
  source_label?: string;
  show?: { eyebrow?: string; title: string; rows?: ReadonlyArray<{ label: string; path: string }> };
  /** Phase 524 — a list of candidates to pick from, instead of one found record. */
  choose?: LookupChoose;
  /**
   * Phase 524 — the label on this lookup's own control. ABSENT ⇒ the renderer's default lookup
   * label, so every lookup declared before this key renders byte-identically. It exists because a
   * step may carry TWO lookups over the same record — one that finds a candidate and one that
   * verifies the chosen identifier — and two controls with the same word on them cannot be read.
   */
  action_label?: string;
  /**
   * Phase 561 — RUN ON ARRIVAL: the declaration's opt-in to the renderer running this lookup
   * ONCE, by itself, when its step renders with every request arg already resolvable (answers
   * pre-filled from context or carried from an earlier step), so the subject sees the result
   * without pressing the control. It dispatches AT MOST ONCE PER FORM SESSION — never again on
   * a keystroke, a re-render or a step re-entry — and a failed or empty result leaves the
   * control live, so re-searching stays the subject's own press. A lookup that is another
   * lookup's `choose.then_run` target never auto-runs — the chain runs it.
   *
   * ABSENT (or `false`) ⇒ the lookup runs only when the subject presses its control,
   * byte-identical to today; the renderer never spends an upstream call the declaration did
   * not ask for.
   */
  auto_run?: boolean;
  /**
   * Card #2717 — A LIST, NOTHING TO TYPE: on a `choose` lookup that auto-runs, the trigger field
   * renders as the HEADING of the list (its label and help) with NO input box, so nothing typed
   * can clear the list and the field is never submitted with a value. The lookup's own control
   * still renders beneath the list, so a list that failed to load can be asked for again.
   *
   * ABSENT (or `false`) ⇒ the trigger field renders its input, byte-identical to today.
   */
  list_only?: boolean;
  /**
   * Card #2878 — the plain sentence for a not_found answer. The server SENDS it on that answer (the
   * renderer shows the server's `message` as it always has); typed here for completeness only.
   */
  not_found_message?: string;
  /**
   * Card #2878 — the staff submit door re-runs this lookup against the submitted answers and refuses
   * the submit unless it answers verified. Server-side only: the renderer shows the refusal sentence
   * it is sent, on the chained pick and on a refused submit.
   */
  verify_on_submit?: boolean;
}

// ── Steps ──────────────────────────────────────────────────────────────────────────────────

/**
 * One ordered step. A step with no `fields` is a CONTENT-ONLY step (guidance / interstitial
 * copy); a step must carry `fields` or `content` (or both).
 */
export interface StepCard {
  title: string;
  content: string;
  /**
   * Phase 532 — a CONDITIONAL BANNER: the card renders only when the predicate holds. The
   * EXISTING bounded predicate on the EXISTING evaluator (`schema/predicates.ts`) — no new
   * block, no new operator, no new component. ABSENT ⇒ the card renders unconditionally.
   */
  visible_when?: VisibleWhen;
  /**
   * Phase 532 — READ-ONLY CTX VALUES IN THE CARD: the label/value pairs the reader needs in
   * front of them before they answer (an assessment ledger, a computed band, a total).
   *
   * The SAME row a `from_ctx.evidence` entry is ({@link FieldEvidenceRef}) — caption, path and
   * (Phase 559) what it shows — on the same path-only `${ctx.…}` grammar, resolved against the
   * SAME served ctx snapshot ({@link ValidateResponse.context}) the predicate evaluator already
   * reads — no new block, no new component, no new predicate.
   *
   * DISPLAY-ONLY: never an answer, never validated, never submitted. A path the snapshot does
   * not carry renders as an explicit "not recorded", never as a blank row.
   */
  rows?: FieldEvidenceRef[];
}
export interface FormStep {
  id: string;
  title?: string;
  content?: string;
  /**
   * Phase 449 — THE REVIEW STEP ("Check your answers"). It renders the ANSWER SUMMARY
   * (`schema/formModel.ts` `answerSummary`) INSTEAD of fields of its own, so it carries no
   * `fields` and no `lookups` (both are deploy-time errors), and at most one step per form
   * declares it. `content` remains its optional intro copy. ABSENT ⇒ today's wizard.
   */
  review?: boolean;
  /** Hides the whole step — and its fields' values — from the payload when false. */
  visible_when?: VisibleWhen;
  fields?: FieldSpec[];
  lookups?: FormLookup[];
  section?: string;
  note?: string;
  cards?: StepCard[];
  next_steps?: string[];
  declaration?: string;
}

/**
 * The `forms.<id>` interior. BOTH shapes are first-class: the legacy flat `fields:` list
 * (no `steps:`) and the multi-step `steps:` list. Neither key ⇒ a description-only form.
 */
export interface FormSchema {
  title?: string;
  description?: string;
  fields?: FieldSpec[];
  steps?: FormStep[];
  submit_label?: string;
  submitted?: SubmittedCopy;
}

/**
 * Copy for the thank-you screen (mirrors the grammar's `FormSubmittedCopy` 1:1). Every key is
 * optional; a form declaring none gets the static success card.
 */
export interface SubmittedCopy {
  title?: string;
  body?: string;
  /**
   * Phase 569 — the declared follow-on link. ADDITIVE and OPT-IN BY PRESENCE: absent ⇒ the
   * thank-you screen is exactly today's and no follow-on call is ever made.
   */
  follow_on?: SubmittedFollowOn;
}

/**
 * Mirrors the grammar's `FormSubmittedFollowOn` 1:1 — ALL SIX keys REQUIRED once the block is
 * present (ARD-DSL-065 validates them at deploy time). The renderer never resolves `source` or
 * checks `allowed_domains` itself: the public follow-on endpoint does both server-side and hands
 * back either a vetted https URL or nothing. They are mirrored so the type stays 1:1 with the
 * declaration; every word the subject reads (`label`, `pending`, `fallback`) is declared here.
 */
export interface SubmittedFollowOn {
  /** The link text. Plain text. */
  label: string;
  /** A bare `${ctx.<dotted.path>}` — resolved by the ENGINE only, never by the renderer. */
  source: string;
  /** Hosts the served URL may point at. Enforced by the ENGINE only. */
  allowed_domains: string[];
  /** Copy shown while the screen polls for the link. */
  pending: string;
  /** Copy shown when no link is served in time. */
  fallback: string;
  /** The poll budget, an xs:duration (<= PT2M, the platform cap). */
  poll_for: string;
}

// ── Values ─────────────────────────────────────────────────────────────────────────────────

/**
 * One repeating-group row: subfield name → its cell. A cell is a STRING for every subfield
 * type but one — that is the `Array<Record<string, string>>` shape the platform's
 * party-resolution step consumes downstream, so a `boolean` subfield submits `"true"` / `""`
 * and a `checkbox_group` subfield submits its selections joined by GROUP_LIST_SEPARATOR.
 *
 * Phase 541 widens the cell by exactly one arm: an `address` subfield holds the SAME nested
 * {@link AddressValue} object a TOP-LEVEL address field already submits. An address has five
 * named parts and a lookup fills all of them; flattening it into one cell would throw away the
 * parts on the way in and force every reader to guess them back out. The submit endpoint
 * accepts nested JSON already (its size guard is recursive), so nothing downstream had to
 * change for it.
 *
 * Every OTHER subfield type stays string-valued, which is why a row read must still be told
 * the subfield's TYPE before it can decide what the cell means — see `formModel.fromRowCell`
 * (cell ⇒ value) and its twin `formModel.toRowCellFor` (value ⇒ cell).
 */
export type GroupRow = Record<string, string | AddressValue>;

/**
 * Any answer the subject can give:
 *  - `string`        — string / email / textarea / select / date / number / tel
 *  - `boolean`       — a single checkbox
 *  - `string[]`      — checkbox_group (multi-select)
 *  - `GroupRow[]`    — a repeating group's rows (string cells, plus an `address` subfield's
 *                      own nested {@link AddressValue} — see {@link GroupRow})
 *  - `AddressValue`  — a top-level `address` block
 *
 * The submit endpoint accepts nested JSON (its size guard is recursive), so no flattening.
 */
export type FieldValue = string | boolean | string[] | GroupRow[] | AddressValue;

/** The whole answer set, keyed by declared field name. */
export type FormValues = Record<string, FieldValue>;

export interface Theme {
  primary: string;
  secondary: string;
  logo_url: string;
  favicon_url: string;
  /** KYC-design — optional overrides; absent ⇒ derived from primary (see theme/brandTokens.ts). */
  accent?: string;
  surface_page?: string;
  surface_card?: string;
  text_primary?: string;
  text_secondary?: string;
  /** KYC-design — the practice's footer line. */
  footer_note?: string;
  /** KYC-design — the practice's display name for copy such as "Submit to <name>". */
  display_name?: string;
}

export interface ValidateResponse {
  tenant_id: string;
  tenant_slug: string;
  form_id: string;
  form_schema: FormSchema;
  theme: Theme;
  /**
   * Phase 290 task 2.1 — resolved party id (iteration_key from the form token).
   * Empty string '' for a single-subject token; a real party id for a
   * per-party token. Consumers use this to surface per-party adaptive hints;
   * the mini-form is basic-attributes-only (no document-upload surface).
   */
  iteration_key: string;
  /**
   * Phase 328 wave 3 task 3.2 — the ctx snapshot: ONLY the `ctx.*` paths this schema's
   * `visible_when` predicates actually reference, keyed by the VERBATIM predicate path
   * (including the `ctx.` prefix), so the evaluator resolves a leaf by direct key lookup.
   * A referenced path that does not resolve is OMITTED — absent means "unset". Never the
   * whole job ctx. Absent/`{}` for a schema with no ctx-driven predicates.
   */
  context?: Record<string, unknown>;
  /**
   * Phase 515 task 1.4 — the party snapshot: ONLY the `job_parties.party_ref` keys this
   * schema's own `default_from: party.<key>` pointers name (FormsApi `BuildPartySnapshot`),
   * keyed BARE — one segment, no prefix — because a party key lives in a namespace of its own
   * rather than being a multi-segment path like a `context` key.
   *
   * `null` for a job-level token (the `''` iteration_key sentinel) and for a party-keyed token
   * whose job has no ledger row; `{}` for a schema that declares no `party.` pointer. All three
   * mean "no party default" to the renderer — the distinction is the SERVER's disclosure bound,
   * which is why it is structural there and flattened to `{}` on the scope here.
   */
  party?: Record<string, unknown> | null;
  /**
   * Phase 328 wave 3 task 3.1 — the token's saved draft, or null when nothing is stored.
   * Restore is wave 5's surface; the shape belongs to the contract.
   */
  draft?: FormValues | null;
  /**
   * Phase 572 wave 3 task 3.1 — the gate is not open yet: the form is bound to an approval gate,
   * but the job's current state does not yet wait on that gate. The SPA polls /context until the
   * gate opens, at which point the decision step and blockers appear.
   *
   * Present with a plain-English message when the form is bound but the gate is not open; absent
   * otherwise. The message names no gate, no state, no workflow — just what the approver needs to
   * know. Served only on the staff surface (StaffFormsApi); the token surface never serves it.
   */
  gate_not_open?: { message: string };
  /**
   * Phase 572 wave 3 task 3.3 — the open blockers on a bound approval gate. Present only when the
   * form is bound to a gate that is currently open (not `gate_not_open`) and has at least one
   * unsettled `requires_clear` item. The approver may settle these items on the page without leaving.
   *
   * Each blocker carries its declared label, the gate's declared message, and which of the two
   * actions apply: "Received" (when `can_receive` is true) and "Not required" (when `can_waive` is
   * true). Never offer an action the tool would refuse.
   */
  blockers?: readonly OpenHoldItem[];
}

/**
 * Phase 572 wave 3 task 3.3 — one open blocker item on an approval gate. The approver sees the
 * declared label and message, and may settle it with one of the two actions.
 *
 * No raw item key, form id or status value reaches the screen (L-97 rule d) — the label and
 * message are the only things a person reads.
 */
export interface OpenHoldItem {
  /** The declaring form's id — needed to call the settle endpoint, never shown. */
  readonly form_id: string;
  /** The declared `supply_later[].id` — needed to call the settle endpoint, never shown. */
  readonly item_key: string;
  /** Party scope or "" for job scope — needed to call the settle endpoint, never shown. */
  readonly iteration_key: string;
  /** The declared label — what staff call the item. This is what the approver reads. */
  readonly label: string;
  /** The gate entry's declared refusal message — plain English about what is missing. */
  readonly message: string;
  /** True when the item may be marked "Received" (staff-settled). */
  readonly can_receive: boolean;
  /** True when the item may be marked "Not required" (waivable). */
  readonly can_waive: boolean;
}

export type ValidateError = "invalid" | "expired" | "consumed";
